import Axios, {AxiosRequestConfig} from "axios";
import {useCallback, useEffect, useMemo, useState,} from "react";
import {useBoolean} from "@fluentui/react-hooks";
import {PagedResult, PagingModel} from "../API";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {TenantResource} from "../enums/TenantResource";
import {TBeforeSubmitFormCallback, useTenantResourceSingle,} from "./useTenantResourceSingle";
import {useLocalStorage} from "../helpers/useLocalStorage";
import {FormAction} from "../types/TenantTypes";

interface ITenantResourceListNewConfig<
    TDatabaseModel,
    TDtoModel,
    TReturnModel,
> {
    query?: ITenantResourceQuery<TReturnModel>;
    orderBy?: keyof TDatabaseModel;
    orderDescending?: boolean;
    paging?: PagingModel;
    search?: string;
    additionalIntParameter?: number;
    additionalStringParameter?: string;
}

interface IUserTenantResourceListNew<TDatabaseModel, TDtoModel, TReturnModel> {
    items?: PagedResult<TReturnModel>;
    loading?: boolean;
    getId: (data: TDatabaseModel | TDtoModel | TReturnModel) => any;
    nextPage: () => void;
    previousPage: () => void;
}

export const useTenantResourceListNew = <
    TDatabaseModel,
    TDtoModel,
    TReturnModel,
>(data: {
    resource: TenantResource;
    queryConfig?: ITenantResourceListNewConfig<
        TDatabaseModel,
        TDtoModel,
        TReturnModel
    >;
    queryIsRequired?: boolean;
    skipLoading?: boolean;
    getId?: (data: TDatabaseModel | TDtoModel | TReturnModel) => any;
    loadResource?: () => void;
}): IUserTenantResourceListNew<TDatabaseModel, TDtoModel, TReturnModel> => {
    if (!data.getId) data.getId = (d: any) => d?.["id"];

    const [items, setItems] = useState<PagedResult<TReturnModel>>();
    const [queryConfig, setQueryConfig] = useState<
        | ITenantResourceListNewConfig<TDatabaseModel, TDtoModel, TReturnModel>
        | undefined
    >(data.queryConfig);
    const [error, setError] = useState<any>();
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const loadResource = useCallback(
        () =>
            new Promise<PagedResult<TReturnModel>>((resolve, reject) => {
                // console.log("Loading resource", data.queryIsRequired, queryConfig);
                if (data.queryIsRequired && !queryConfig) return;

                if (!data.skipLoading) {
                    setLoading();
                }

                const body: any = {};
                if (queryConfig?.paging) {
                    body.paging = {
                        count: queryConfig?.paging?.count ?? 30,
                        page: queryConfig?.paging?.page ?? 0,
                    };
                } else {
                    body.paging = {count: 0, page: -1};
                }

                body.query = queryConfig?.query;
                const search = queryConfig?.search;
                body.orderBy = queryConfig?.orderBy;
                body.orderDescending = queryConfig?.orderDescending;
                body.search = search;

                const pars: AxiosRequestConfig = {
                    params: {
                        search,
                    },
                };

                if (queryConfig?.additionalIntParameter) {
                    pars.params["additionalIntParameter"] =
                        queryConfig?.additionalIntParameter;
                }

                if (queryConfig?.additionalStringParameter) {
                    pars.params["additionalStringParameter"] =
                        queryConfig?.additionalStringParameter;
                }

                Axios.post<PagedResult<TReturnModel>>(
                    data.resource.split("+")[0] + "/List",
                    body,
                    pars,
                )
                    .then(({data}) => {
                        resolve(data);
                        setItems(data);
                        setQueryConfig(queryConfig);
                        setError(undefined);
                    })
                    .catch((error) => {
                        let e;
                        if (error.response && error.response.data) {
                            e = error.response.data;
                        } else if (error.response) {
                            e = error.response;
                        } else {
                            e = error;
                        }
                        setError(e);
                        reject(e);
                    })
                    .finally(() => {
                        if (!data.skipLoading) {
                            setNotLoading();
                        }
                    });
            }),
        [
            data.queryIsRequired,
            data.skipLoading,
            data.resource,
            setLoading,
            setNotLoading,
        ],
    );

    const nextPage = useCallback(() => {
        setQueryConfig({
            ...queryConfig,
            paging: {
                ...queryConfig?.paging,
                count: queryConfig?.paging?.count ?? 0,
                page: (queryConfig?.paging?.page ?? 0) + 1,
            },
        });
    }, [queryConfig]);

    const previousPage = useCallback(() => {
        setQueryConfig({
            ...queryConfig,
            paging: {
                ...queryConfig?.paging,
                count: queryConfig?.paging?.count ?? 0,
                page: (queryConfig?.paging?.page ?? 0) - 1,
            },
        });
    }, [queryConfig]);

    return {
        items,
        loading,
        getId: data.getId,
        nextPage,
        previousPage,
    };
};

interface IUseTenantResourceList<DatabaseModel, DtoModel, TReturn> {
    query?: ITenantResourceQuery<TReturn>;
    setQuery: (q?: ITenantResourceQuery<TReturn>) => void;
    appendQuery: (q?: ITenantResourceQuery<TReturn>) => void;
    result?: PagedResult<TReturn>;
    reload: () => Promise<PagedResult<TReturn>>;
    reloadSingle: (
        i: TReturn,
        additionalIntParameter?: number,
        additionalStringParameter?: string,
    ) => Promise<TReturn>;
    selectedItem?: Partial<TReturn>;
    setSelectedItem: (i?: TReturn) => void;
    removeSelected: (override?: TReturn) => Promise<void>;
    action: <TData, TResponse>(
        route: string,
        data: TData,
        reload?: boolean,
    ) => Promise<TResponse>;
    nextPage: () => void;
    showAll: () => void;
    previousPage: () => void;
    paging: PagingModel;
    setSearch: (search?: string) => void;
    search?: string;
    getId?: (data: any) => any;
    setOrderBy: (col?: keyof DatabaseModel, orderDescending?: boolean) => void;
    setOrderDescending: (b?: boolean) => void;
    orderBy?: keyof DatabaseModel;
    orderDescending?: boolean;
    uniquenessId?: string;
    error?: any;
    loading?: boolean;
    skipLoading?: boolean;
    duplicate: (item: Partial<TReturn>) => Promise<TReturn>;
    appendOrPositionModel: (data: TReturn) => void;
    clearResult: () => void;
    clearQuery: () => void;
}

interface IUseTenantResourceListQuery<DatabaseModel, DtoModel, TReturn> {
    query?: ITenantResourceQuery<TReturn>;
    search?: string;
    orderDescending?: boolean;
    orderBy?: keyof DatabaseModel;
}

export const useTenantResourceListParametrized = <
    DatabaseModel,
    DtoModel,
    TReturn,
>(data: {
    resource: TenantResource;
    cacheKey: string;
    initialQuery?: ITenantResourceQuery<TReturn>;
    queryIsRequired?: boolean;
    paginate?: boolean;
    singularPagination?: boolean;
    initialOrderBy?: keyof DatabaseModel;
    initialOrderDescending?: boolean;
    getId?: (data: any) => any;
    additionalIntParameter?: number;
    additionalStringParameter?: string;
    afterFormAction?: (formAction: FormAction, dto: TReturn) => void;
    skipLoading?: boolean;
    initialFullQuery?: IUseTenantResourceListQuery<
        DatabaseModel,
        DtoModel,
        TReturn
    >;
    loadOnMount?: boolean;
    ignoreTenancy?: boolean;
    beforeSubmitForm?: (
        original: Partial<TReturn> | undefined,
        dto: DtoModel,
    ) => Promise<boolean>; // boolean vrati kao continue?
    clearResult?: () => void;
    clearQuery?: () => void;
}): IUseTenantResourceList<DatabaseModel, DtoModel, TReturn> => {
    return useTenantResourceList(
        data.resource,
        data.cacheKey,
        data.initialQuery,
        data.queryIsRequired,
        data.paginate,
        data.initialOrderBy,
        data.initialOrderDescending,
        data.getId,
        data.additionalIntParameter,
        data.additionalStringParameter,
        data.afterFormAction,
        data.skipLoading,
        data.singularPagination,
        data.initialFullQuery,
        data.loadOnMount,
        data.ignoreTenancy,
        data.beforeSubmitForm,
    );
};

export const useTenantResourceList = <DatabaseModel, DtoModel, TReturn>(
    resource: TenantResource,
    cacheKey: string,
    initialQuery?: ITenantResourceQuery<TReturn>,
    queryIsRequired?: boolean,
    paginate?: boolean,
    initialOrderBy?: keyof DatabaseModel,
    initialOrderDescending?: boolean,
    getId: (data: any) => any = (data) => data["id"],
    additionalIntParameter?: number,
    additionalStringParameter?: string,
    afterFormAction?: (formAction: FormAction, dto: TReturn) => void,
    skipLoading?: boolean,
    singularPagination?: boolean,
    initialFullQuery?: IUseTenantResourceListQuery<
        DatabaseModel,
        DtoModel,
        TReturn
    >,
    loadOnMount?: boolean,
    ignoreTenancy?: boolean,
    beforeSubmitForm?: TBeforeSubmitFormCallback<DtoModel, TReturn>, // boolean vrati kao continue?
    headers?: { [key: string]: string },
): IUseTenantResourceList<DatabaseModel, DtoModel, TReturn> => {
    const [items, setItems] = useState<PagedResult<TReturn>>();
    const {model, setModel, deleteModel, loadModel} = useTenantResourceSingle<
        DatabaseModel,
        DtoModel,
        TReturn
    >(
        resource,
        undefined,
        undefined,
        getId,
        additionalIntParameter,
        additionalStringParameter,
        afterFormAction,
        undefined,
        beforeSubmitForm,
    );
    const [query, setQuery] = useLocalStorage<
        IUseTenantResourceListQuery<DatabaseModel, DtoModel, TReturn>
    >(
        cacheKey,
        initialFullQuery ?? {
            query: initialQuery,
            orderDescending: initialOrderDescending,
            orderBy: initialOrderBy,
        },
    );
    const [error, setError] = useState<any>();
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const loadResource = useCallback(
        (
            pageOverride?: number,
            queryOverride?: ITenantResourceQuery<TReturn>,
            searchOverride?: string,
            queryOrderOverride?: { orderBy?: string; orderDescending?: boolean },
        ): Promise<PagedResult<TReturn>> =>
            new Promise<PagedResult<TReturn>>((resolve, reject) => {
                // console.log("Loading resource", queryIsRequired, query, queryOverride);
                if (queryIsRequired && !query.query && !queryOverride) return;
                if (!skipLoading) {
                    setLoading();
                }

                const body: any = {};
                if (singularPagination && paginate)
                    alert("You can only use singularPagination or paginate not both");
                if (singularPagination) {
                    body.paging = {
                        count: 1,
                        page: pageOverride !== undefined ? pageOverride : 0,
                    };
                } else if (paginate) {
                    if (items) {
                        body.paging = {
                            count: items.count,
                            page: pageOverride !== undefined ? pageOverride : items.page,
                        };
                    } else {
                        body.paging = {
                            count: 30,
                            page: pageOverride !== undefined ? pageOverride : 0,
                        };
                    }
                } else {
                    body.paging = {count: 0, page: -1};
                }

                body.query = queryOverride !== undefined ? queryOverride : query.query;
                const search =
                    searchOverride !== undefined ? searchOverride : query.search;
                body.orderBy = queryOrderOverride?.orderBy ?? query.orderBy;
                body.orderDescending =
                    queryOrderOverride?.orderDescending ?? query.orderDescending;
                body.search = search;

                const pars: AxiosRequestConfig = {
                    params: {
                        search,
                    },
                };

                pars.headers = {
                    ...pars.headers,
                    ...headers,
                };

                if (ignoreTenancy) {
                    pars.headers["IgnoreTenancy"] = ignoreTenancy;
                }

                if (additionalIntParameter) {
                    pars.params["additionalIntParameter"] = additionalIntParameter;
                }

                if (additionalStringParameter) {
                    pars.params["additionalStringParameter"] = additionalStringParameter;
                }

                Axios.post<PagedResult<TReturn>>(
                    resource.split("+")[0] + "/List",
                    body,
                    pars,
                )
                    .then(({data}) => {
                        resolve(data);
                        setItems(data);
                        const newQuery: IUseTenantResourceListQuery<
                            DatabaseModel,
                            DtoModel,
                            TReturn
                        > = {
                            ...query,
                            query: body.query,
                            search: body.search,
                            orderBy: body.orderBy,
                            orderDescending: body.orderDescending,
                        };
                        setQuery(newQuery);
                        setError(undefined);
                    })
                    .catch((error) => {
                        let e;
                        if (error.response && error.response.data) {
                            e = error.response.data;
                        } else if (error.response) {
                            e = error.response;
                        } else {
                            e = error;
                        }
                        setError(e);
                        reject(e);
                    })
                    .finally(() => {
                        if (!skipLoading) {
                            setNotLoading();
                        }
                    });
            }),
        [
            resource,
            queryIsRequired,
            items,
            query,
            additionalIntParameter,
            additionalStringParameter,
            skipLoading,
            singularPagination,
            ignoreTenancy,
            headers,
        ],
    );

    const reload = useCallback<() => Promise<PagedResult<TReturn>>>(
        () =>
            new Promise((resolve, reject) => {
                loadResource().then(resolve).catch(reject);
            }),
        [
            loadResource,
            queryIsRequired,
            resource,
            paginate,
            additionalIntParameter,
            additionalStringParameter,
        ],
    );

    useEffect(() => {
        if (loadOnMount) {
            reload();
        }
    }, [loadOnMount]);
    const appendOrPositionModel = useCallback(
        (data: TReturn) => {
            const itemId = getId(data);

            setItems((items) => {
                if (!items) return undefined;

                // Find the index of the item with itemId
                const index = items.items.findIndex((m) => getId(m) == itemId);

                // If the item exists, replace it
                if (index !== -1) {
                    items.items[index] = data;
                } else {
                    const propertyToOrderBy = query.orderBy;
                    let compareValue: any;

                    if (propertyToOrderBy && (data as any)[propertyToOrderBy]) {
                        compareValue = (data as any)[propertyToOrderBy];
                    } else {
                        // If neither orderBy nor orderDescending are set, append data to the end
                        items.items.push(data);
                        return {
                            ...items,
                            items: items.items,
                        };
                    }

                    // Determine where to insert the new item based on query.orderBy and query.orderDescending
                    let insertIndex;
                    if (query.orderDescending) {
                        insertIndex = items.items.findIndex(
                            (m) => (m as any)[propertyToOrderBy] < compareValue,
                        );
                    } else {
                        insertIndex = items.items.findIndex(
                            (m) => (m as any)[propertyToOrderBy] > compareValue,
                        );
                    }

                    // If we didn't find a position, append it to the end or beginning based on orderDescending
                    if (insertIndex === -1) {
                        if (query.orderDescending) {
                            items.items.unshift(data);
                        } else {
                            items.items.push(data);
                        }
                    } else {
                        items.items.splice(insertIndex, 0, data);
                    }
                }

                return {
                    ...items,
                    items: items.items,
                };
            });
        },
        [items, getId, setItems, query.orderDescending, query.orderBy],
    );

    const reloadSingle = useCallback<
        (
            item: TReturn,
            additionalIntParameter?: number | undefined,
            additionalStringParameter?: string | undefined,
        ) => Promise<TReturn>
    >(
        (item) =>
            new Promise((resolve, reject) => {
                const itemId = getId(item);

                if (!skipLoading) {
                    setLoading();
                }
                Axios.get<TReturn>(resource.split("+")[0] + "/" + itemId, {
                    params: {additionalIntParameter, additionalStringParameter},
                })
                    .then(({data}) => {
                        resolve(data);
                        appendOrPositionModel(data);
                    })
                    .catch(({response: {data}}) => {
                        reject(data);
                    })
                    .finally(() => {
                        if (!skipLoading) {
                            setNotLoading();
                        }
                    });
            }),
        [
            loadModel,
            getId,
            items,
            additionalIntParameter,
            additionalStringParameter,
            appendOrPositionModel,
        ],
    );

    const removeSelected = useCallback(
        (override?: TReturn) =>
            new Promise<void>((resolve, reject) => {
                if (!skipLoading) {
                    setLoading();
                }
                deleteModel(override)
                    .then(() => {
                        resolve();
                        reload();
                    })
                    .catch(reject)
                    .finally(() => {
                        if (!skipLoading) {
                            setNotLoading();
                        }
                    });
            }),
        [deleteModel, reload, skipLoading],
    );

    const action = useCallback(
        <TData, TResponse>(a: string, d: TData, reloadData?: boolean) =>
            new Promise<TResponse>((resolve, reject) => {
                if (!skipLoading) {
                    setLoading();
                }
                Axios.post(resource.split("+")[0] + "/" + a, d)
                    .then(({data}) => {
                        resolve(data);
                        if (reloadData) reload();
                    })
                    .catch(reject)
                    .finally(() => {
                        if (!skipLoading) {
                            setNotLoading();
                        }
                    });
            }),
        [resource, reload],
    );

    const paging = useMemo(() => {
        if (items) {
            return {page: items.page, count: items.count};
        }

        return {page: 0, count: 30};
    }, [items]);

    const nextPage = useCallback(() => {
        loadResource(paging.page + 1, undefined, query.search).then();
    }, [paging, loadResource, query.search]);

    const previousPage = useCallback(() => {
        if (paging.page > 0)
            loadResource(paging.page - 1, undefined, query.search).then();
    }, [paging, loadResource, query.search]);

    const changeQuery = useCallback(
        (q?: ITenantResourceQuery<TReturn>) => {
            if (q) {
                // This void callback owns the request; loadResource exposes the
                // failure through error. Awaited reload callers still reject.
                void loadResource(0, q, query.search).catch(() => {});
            }
        },
        [loadResource, query.search],
    );

    const appendQuery = useCallback(
        (q?: ITenantResourceQuery<TReturn>) => {
            if (q) {
                loadResource(0, {...query.query, ...q}, query.search).then();
            }
        },
        [loadResource, query],
    );

    const setSearch = useCallback(
        (s?: string) => {
            if (s != query?.search) {
                loadResource(0, undefined, s).then();
            }
        },
        [loadResource, query],
    );

    const setOrderBy = useCallback(
        (s?: keyof DatabaseModel, orderDescendingOverride?: boolean) => {
            const nextOrderDescending =
                orderDescendingOverride !== undefined
                    ? orderDescendingOverride
                    : s
                        ? query.orderDescending
                        : undefined;
            if (s != query?.orderBy || nextOrderDescending !== query?.orderDescending) {
                loadResource(0, undefined, query.search, {
                    orderDescending: nextOrderDescending,
                    orderBy: s as any,
                });
            }
        },
        [loadResource, query],
    );

    const setOrderDescending = useCallback(
        (b?: boolean) => {
            if (b != query?.orderDescending) {
                loadResource(0, undefined, query.search, {
                    orderBy: query.orderBy as any,
                    orderDescending: b,
                });
            }
        },
        [loadResource, query],
    );

    const showAll = useCallback(() => {
        loadResource(-1, undefined, query.search).then();
    }, [loadResource, query.search]);

    const duplicate = useCallback(
        (item: Partial<TReturn>) =>
            new Promise<TReturn>((resolve, reject) => {
                if (!skipLoading) {
                    setLoading();
                }
                Axios.post<TReturn>(
                    `${resource.split("+")[0]}/Duplicate/${getId(item)}`,
                    {},
                    {
                        params: {
                            additionalIntParameter,
                            additionalStringParameter,
                        },
                    },
                )
                    .then(({data}) => {
                        resolve(data);
                        reload();
                    })
                    .catch(reject)
                    .finally(() => {
                        if (!skipLoading) {
                            setNotLoading();
                        }
                    });
            }),
        [
            skipLoading,
            resource,
            getId,
            additionalIntParameter,
            additionalStringParameter,
            setLoading,
            reload,
            setNotLoading,
        ],
    );

    const clearResult = useCallback(() => {
        setItems(undefined);
    }, [setItems]);

    const clearQuery = useCallback(() => {
        setQuery({
            ...query,
            query: undefined,
        });
    }, [setQuery, query]);

    return {
        query: query.query,
        setQuery: changeQuery,
        appendQuery,
        result: items,
        reload,
        selectedItem: model,
        setSelectedItem: setModel,
        removeSelected,
        action,
        nextPage,
        previousPage,
        paging,
        setSearch,
        search: query?.search,
        setOrderBy,
        setOrderDescending,
        orderBy: query.orderBy,
        orderDescending: query.orderDescending,
        showAll,
        error,
        loading,
        reloadSingle,
        duplicate,
        appendOrPositionModel,
        clearResult,
        clearQuery,
    };
};
